/**
 * Behavioural segment list types and default params (London Smoke & Cure).
 * Params are operator-setupable in Admin Segments; predicates live in segmentEvaluatorRegistry.
 */

import { DEFAULT_RECENT_ACCOUNT_WITHIN_DAYS } from './segmentAudienceConstants';

export const SEGMENT_TYPE_ORDER_ACTIVITY = 'order_activity';
export const SEGMENT_TYPE_ACCOUNT_RECENT = 'account_recent';

export const SEGMENT_TYPES = Object.freeze({
  [SEGMENT_TYPE_ORDER_ACTIVITY]: {
    label: 'Order activity',
    description:
      'Wholesale accounts with enough orders in a rolling window (trading customers).',
  },
  [SEGMENT_TYPE_ACCOUNT_RECENT]: {
    label: 'Recent accounts',
    description: 'Users created within the last N days (welcome / onboarding).',
  },
});

/** Default quiet window (days since last order) for dormant segment bootstraps. Editable per list in Admin. */
export const DEFAULT_DORMANT_QUIET_DAYS = 90;

/**
 * Bootstrap Firestore list document ids (operator or Ensure defaults).
 * Legacy v1: segmentType + params. Composite v2: schemaVersion 2 + definition.
 */
export const DEFAULT_SEGMENT_LIST_BOOTSTRAPS = Object.freeze([
  {
    id: 'segment_active_traders',
    name: 'Active traders',
    description: 'System segment — members with recent order activity.',
    segmentType: SEGMENT_TYPE_ORDER_ACTIVITY,
    params: {
      minOrders: 1,
      withinDays: 90,
      requireAccountActive: true,
      excludeSuppression: true,
    },
  },
  {
    id: 'segment_new_accounts',
    name: 'Recent accounts',
    description: 'System segment — new wholesale accounts (signup window).',
    segmentType: SEGMENT_TYPE_ACCOUNT_RECENT,
    params: {
      withinDays: DEFAULT_RECENT_ACCOUNT_WITHIN_DAYS,
      requireAccountActive: false,
      excludeSuppression: true,
    },
  },
  {
    id: 'segment_dormant_traders',
    name: 'Dormant traders',
    description:
      'Ordered at least once, then quiet (default 90 days since last order). Change days_since_last_order in Admin composite rules.',
    schemaVersion: 2,
    definition: {
      enabled: true,
      schemaVersion: 2,
      match: 'all',
      predicates: [
        { type: 'lifetime_order_count', params: { minOrders: 1 } },
        { type: 'days_since_last_order', params: { minDays: DEFAULT_DORMANT_QUIET_DAYS } },
      ],
      gates: { excludeSuppression: true, requireAccountFlagActive: true },
      annotations: { computeChips: true, membershipChip: 'Dormant trader' },
    },
  },
  {
    id: 'segment_dormant_one_off',
    name: 'Dormant one-offs',
    description:
      'Exactly one lifetime order, then quiet (default 90 days). Subset of dormant traders for sharper win-back. Edit quiet window in Admin.',
    schemaVersion: 2,
    definition: {
      enabled: true,
      schemaVersion: 2,
      match: 'all',
      predicates: [
        { type: 'lifetime_order_count', params: { minOrders: 1, maxOrders: 1 } },
        { type: 'days_since_last_order', params: { minDays: DEFAULT_DORMANT_QUIET_DAYS } },
      ],
      gates: { excludeSuppression: true, requireAccountFlagActive: true },
      annotations: { computeChips: true, membershipChip: 'Dormant one-off' },
    },
  },
]);

/** Production segment list ids — never offered in prototype cleanup. */
export const CANONICAL_SEGMENT_LIST_IDS = Object.freeze(
  DEFAULT_SEGMENT_LIST_BOOTSTRAPS.map((b) => b.id),
);

const ORDER_ACTIVITY_DEFAULTS = {
  minOrders: 1,
  withinDays: 90,
  requireAccountActive: true,
  excludeSuppression: true,
};

const ACCOUNT_RECENT_DEFAULTS = {
  withinDays: DEFAULT_RECENT_ACCOUNT_WITHIN_DAYS,
  requireAccountActive: false,
  excludeSuppression: true,
};

/**
 * @param {string} type
 * @returns {Record<string, unknown>}
 */
export function defaultParamsForSegmentType(type) {
  if (type === SEGMENT_TYPE_ACCOUNT_RECENT) {
    return { ...ACCOUNT_RECENT_DEFAULTS };
  }
  return { ...ORDER_ACTIVITY_DEFAULTS };
}

/**
 * Merge Firestore segment.params with type defaults.
 * @param {string} type
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function mergeSegmentParams(type, raw) {
  const base = defaultParamsForSegmentType(type);
  const p = raw && typeof raw === 'object' ? raw : {};
  const out = { ...base };

  if (type === SEGMENT_TYPE_ORDER_ACTIVITY) {
    const minOrders = Number(p.minOrders);
    if (Number.isFinite(minOrders) && minOrders >= 0) out.minOrders = Math.floor(minOrders);
    const withinDays = Number(p.withinDays);
    if (Number.isFinite(withinDays) && withinDays > 0) out.withinDays = Math.floor(withinDays);
    if (typeof p.requireAccountActive === 'boolean') out.requireAccountActive = p.requireAccountActive;
    if (typeof p.excludeSuppression === 'boolean') out.excludeSuppression = p.excludeSuppression;
  }

  if (type === SEGMENT_TYPE_ACCOUNT_RECENT) {
    const withinDays = Number(p.withinDays);
    if (Number.isFinite(withinDays) && withinDays > 0) out.withinDays = Math.floor(withinDays);
    if (typeof p.requireAccountActive === 'boolean') out.requireAccountActive = p.requireAccountActive;
    if (typeof p.excludeSuppression === 'boolean') out.excludeSuppression = p.excludeSuppression;
  }

  return out;
}

/**
 * Stable hash for audit / campaign metadata (not cryptographic).
 * @param {string} type
 * @param {Record<string, unknown>} params
 */
export function hashSegmentParams(type, params) {
  try {
    const payload = JSON.stringify({ type, params: mergeSegmentParams(type, params) });
    let h = 0;
    for (let i = 0; i < payload.length; i++) {
      h = (Math.imul(31, h) + payload.charCodeAt(i)) | 0;
    }
    return `seg_${type}_${(h >>> 0).toString(16)}`;
  } catch {
    return `seg_${type}_unknown`;
  }
}

/**
 * @param {unknown} listDocOrId
 * @param {object} [listData] when first arg is id string
 */
export function isSystemSegmentList(listDocOrId, listData) {
  let data = listData;
  if (listDocOrId && typeof listDocOrId === 'object' && !listData) {
    data = listDocOrId;
  }
  if (!data || typeof data !== 'object') return false;
  const seg = data.segment;
  if (data.systemManaged !== true || seg?.enabled !== true) return false;
  if (seg.type) return true;
  return (
    Number(seg.schemaVersion) >= 2 &&
    Array.isArray(seg.predicates) &&
    seg.predicates.length > 0
  );
}

/**
 * Human-readable params summary for CM / Admin UI.
 * @param {string} type
 * @param {Record<string, unknown>} params
 */
export function formatSegmentParamsBrief(type, params) {
  const p = mergeSegmentParams(type, params);
  if (type === SEGMENT_TYPE_ORDER_ACTIVITY) {
    const parts = [
      `≥${p.minOrders} order(s) in ${p.withinDays} days`,
      p.requireAccountActive ? 'active account' : 'any account status',
    ];
    if (p.excludeSuppression) parts.push('excludes DNC / unsubscribes');
    return parts.join(' · ');
  }
  if (type === SEGMENT_TYPE_ACCOUNT_RECENT) {
    const parts = [`created in last ${p.withinDays} days`];
    if (p.requireAccountActive) parts.push('active only');
    if (p.excludeSuppression) parts.push('excludes DNC / unsubscribes');
    return parts.join(' · ');
  }
  return type || '—';
}
